import { response, TaskState } from "@iflyrpa/share";
import { AxiosProxyParams, MockPublish } from "../../types";
import { stringifyError } from "../../utils";
import { DouyinImageUploader } from "../../utils/douyin/imageUploader";
import type { ParsedVerifyDecision } from "../../utils/douyin/verify-parser";
import { needsVerification, parseVerifyPassportDecision } from "../../utils/douyin/verify-parser";
import { Http } from "../../utils/http";
import { reportLogger } from "../../utils/reportLogger";
import type { PublishAction } from "./index";

const { generateCsrfTokenAdvanced } = require("../../utils/douyin/douyinSafe/csrfToken.js");
const { getABogus } = require("../../utils/douyin/douyinSafe/a_bogus.js");
const { generateDtrait } = require("../../utils/douyin/douyinSafe/dtrait.js");
const { getBdV2 } = require("../../utils/douyin/douyinSafe/bd_para.js");

interface CommonResponse<T = unknown> {
    status_code: any;
    data: any;
    status_msg: string;
    item_id: string;
}

//生成creation_id 8位字母与数字混淆随机数+当前时间戳
function generateRandomString(length: number) {
    const characters = "abcdefghijklmnopqrstuvwxyz0123456789";
    let randomString = "";
    for (let i = 0; i < length; i++) {
        const randomIndex = Math.floor(Math.random() * characters.length);
        randomString += characters.charAt(randomIndex);
    }
    return randomString;
}
// @ts-expect-error
export const mockAction: PublishAction = async (task, params) => {
    const updateTaskState = task.taskStageStore?.update?.bind(task.taskStageStore, task.taskId || "");

    if (
        !params.extraParam ||
        !params.extraParam["security-sdk/s_sdk_pri_key"] ||
        !params.extraParam["security-sdk/s_sdk_pub_key"] ||
        !params.extraParam["security-sdk/s_sdk_sign_data_key/web_protect"] ||
        !params.extraParam["security-sdk/s_sdk_server_cert_key"] ||
        !params.extraParam["security-sdk/s_sdk_crypt_sdk"]
    ) {
        return response(414, "extraParam 参数缺失或不完整", "");
    }
    const userAgent =
        params.userAgent ||
        "Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/134.0.0.0 Safari/537.38";

    // return response(500, "获取上传地址失败", "");
    const sessionidCookie = params.cookies.find((it: any) => it.name === "msToken")?.value;
    if (!sessionidCookie) {
        return { code: 414, message: "账号数据异常，请重新绑定账号后重试。", data: "" };
    }

    const headers = {
        cookie: params.cookies.map((it: any) => `${it.name}=${it.value}`).join(";"),
        origin: "https://creator.douyin.com",
        referer:
            "https://creator.douyin.com/creator-micro/content/post/image?default-tab=3&enter_from=publish_page&media_type=image&type=new",
        Accept: "application/json, text/plain, */*",
        "Accept-Encoding": "gzip, deflate, br, zstd",
        "Content-Type": "application/json",
        pragma: "no-cache",
        "cache-control": "no-cache",
        "sec-ch-ua-platform": params.extraParam?.browserInfo?.["sec-ch-ua-platform"] || "",
        "sec-ch-ua": params.extraParam?.browserInfo?.["sec-ch-ua"] || "",
        "bd-ticket-guard-web-version": "2",
        "sec-ch-ua-mobile": "?0",
        "user-agent": userAgent,
        "sec-fetch-site": "same-origin",
        "sec-fetch-mode": "cors",
        "sec-fetch-dest": "empty",
        "accept-language": "zh-CN,zh;q=0.9",
        priority: "u=1, i",
    };
    const publishParams = {
        read_aid: "2906",
        aid: "1128",
        cookie_enabled: "true",
        screen_width: params.extraParam?.browserInfo.screen_width || "1920",
        screen_height: params.extraParam?.browserInfo.screen_height || "1080",
        browser_language: params.extraParam?.browserInfo.browser_language || "zh-CN",
        browser_platform: params.extraParam?.browserInfo.browser_platform || "Win32",
        browser_name: params.extraParam?.browserInfo.browser_name || "Mozilla",
        browser_version: userAgent,
        browser_online: "true",
        timezone_name: "Asia/Shanghai",
        support_h265: "1",
        msToken: params.cookies.find((e: any) => e.name === "msToken")?.value || "",
        a_bogus: "",
    };
    task.logger.info(`[douyinPublish] publishParams ${JSON.stringify(publishParams)}`);

    const publishData: Record<string, unknown> = {
        item: {
            common: {
                text: params.content,
                text_extra: params.textExtra,
                activity: "[]",
                challenges: params.challengeIds || "[]", // 热点的cid
                hashtag_source: "",
                mentions: "[]", // @好友的uid列表
                music_id: params.musicId || "", // 取id_str TODO
                music_end_time: (params.musicDuration || 0) * 1000, //duration*1000 TODO
                hot_sentence: params.hotSentence || "", // 热点
                visibility_type: Number(params.visibleRange) || 0, // 谁可以看 0-公开 1-仅自己可见 2-好友可见
                download: params.allowSave ? 1 : 0, // 保存权限 1-允许保存 0-不允许
                timing: params.isImmediatelyPublish ? -1 : params.scheduledPublish, // 发布时间 -1立即发布 时间戳-定时发布
                media_type: Number(params.publishType) || 2,
                images: [],
                creation_id: "",
            },
            cover: {
                poster: "",
            },
            mix: params.mix
                ? {
                      mix_id: params.mix.mix_id,
                      mix_order: params.mix.mix_order || 0,
                  }
                : {}, // 合集TODO
            anchor: {},
            declare: {
                user_declare_info: "{}",
            },
        },
    };

    // const args: AxiosProxyParams = [{ headers }, task.logger, params.proxyLoc, params.localIP, params.accountId, "douyin"];
    const args: AxiosProxyParams = [{ headers }, task.logger, "", "", params.accountId, "douyin"];
    const http = new Http({ headers });
    const proxyHttp = new Http(...args);

    // 已上传的图片列表
    const uploadedImgList = params.uploadedImgList ? JSON.parse(params.uploadedImgList) : [];
    if (uploadedImgList.length > 0) {
        (publishData.item as any).common.images = uploadedImgList;
    } else {
        // 创建图片上传管理器
        const tmpCachePath = task.getTmpPath();
        const uploader = new DouyinImageUploader(proxyHttp, headers, publishParams, tmpCachePath);
        //上传封面
        if (params.coverImage) {
            const cover = await uploader.uploadCover(params.coverImage);
            if (cover) {
                (publishData.item as any).common.images.push({
                    uri: cover.uri,
                    width: cover.width,
                    height: cover.height,
                });
                (publishData.item as any).cover = {
                    poster: cover.uri,
                };
            }
        }

        //上传内容
        if (params.banners && params.banners.length > 0) {
            const banners = params.banners.filter((i) => i !== params.coverImage);
            const uploadResults = await uploader.uploadImages(banners);
            (publishData.item as any).common.images = (publishData.item as any).common.images.concat(
                uploadResults.map((result) => ({
                    uri: result.uri,
                    width: result.width,
                    height: result.height,
                })),
            );
        }
    }

    (publishData.item as any).common.creation_id = generateRandomString(8) + Date.now();

    let decision: ParsedVerifyDecision | null = null;
    // task.logger.info(`[douyinPublish] proxyHttp 代理信息: ${proxyHttp.proxyInfo} `);
    proxyHttp.addResponseInterceptor<CommonResponse>((response) => {
        task.logger.info(`[douyinPublish] 拦截器收到响应response.headers: ${JSON.stringify(response.headers)} `);
        task.logger.info(`[douyinPublish] 拦截器收到响应response.data: ${JSON.stringify(response.data)} `);

        // 解析验证决策头
        decision = parseVerifyPassportDecision(response.headers["x-tt-verify-passport-decision"]);
        if (needsVerification(decision)) {
            task.logger.info(`[douyinPublish] 检测到需要验证: ${JSON.stringify(decision)}`);
            return;
        }

        if (!response || !response.data) {
            return;
        }

        const responseData = response.data;
        // 检查响应中的 status_code 字段
        if (response && responseData?.status_code && responseData.status_code !== 0) {
            // 将抖音的服务器错误（status_code: 4）映射为 500，触发重试
            const errorCode = responseData.status_code === 4 ? 500 : responseData.status_code;

            if (responseData.status_code === 4) {
                task.logger.warn(
                    `[douyinPublish] 抖音服务器错误 status_code: 4，映射为 500 触发重试。原始响应: ${JSON.stringify(responseData)}`,
                );
            }

            return {
                code: errorCode,
                message: responseData.status_msg || "文章发布异常，请稍后重试。",
                data: responseData,
            };
        }
    });
    task._timerRecord.PrePublish = Date.now();

    if (MockPublish) {
        const data = "123456789";
        const message = `图文模拟发布成功 ${http.proxyInfo}`;

        await updateTaskState?.({
            state: TaskState.SUCCESS,
            result: {
                response: data,
            },
        });
        return response(0, message, data);
    }

    // 生成 bdTicketGuardClientData
    let webProtectData: any;
    try {
        webProtectData = JSON.parse(
            JSON.parse(params.extraParam["security-sdk/s_sdk_sign_data_key/web_protect"] || "{}").data || "{}",
        );

        if (!webProtectData.ticket || !webProtectData.ts_sign || !webProtectData.client_cert) {
            task.logger.error(`webProtectData 关键字段缺失: ${JSON.stringify(webProtectData)}`);
            return response(414, "安全参数 webProtectData 不完整，请重新获取后重试", "");
        }
    } catch (error) {
        task.logger.error(`解析 webProtectData 失败: ${error}`);
        return response(414, "安全参数 webProtectData 解析失败，请重新获取后重试", "");
    }

    let ticket = webProtectData.ticket;
    let ts_sign = webProtectData.ts_sign;

    // 优先使用 cookie 中的最新 ticket/ts_sign：服务器在二次验证流程后会轮换它们，
    // 并写入 bd_ticket_guard_server_data cookie。而 web_protect 里那份是绑定/登录时
    // 抓取的，验证后会过期，继续用它签名会被服务器拒绝（403，bd-ticket-guard-result: 1002）。
    const serverDataCookie = params.cookies.find((it: any) => it.name === "bd_ticket_guard_server_data")?.value;
    if (serverDataCookie) {
        try {
            const decoded = JSON.parse(Buffer.from(decodeURIComponent(serverDataCookie), "base64").toString("utf8"));
            if (decoded.ticket && decoded.ts_sign) {
                if (decoded.ticket !== ticket) {
                    task.logger.info("[douyinPublish] 使用 cookie 中的最新 ticket（web_protect 已过期）");
                }
                ticket = decoded.ticket;
                ts_sign = decoded.ts_sign;
            }
        } catch (error) {
            task.logger.info(`[douyinPublish] 解析 bd_ticket_guard_server_data 失败，回退到 web_protect: ${error}`);
        }
    }

    let ec_privateKey: string;
    try {
        ec_privateKey = JSON.parse(
            JSON.parse(params.extraParam["security-sdk/s_sdk_crypt_sdk"] || "{}").data || "{}",
        ).ec_privateKey;

        if (!ec_privateKey) {
            task.logger.error("[douyinPublish] ec_privateKey 为空");
            return response(414, "安全参数 ec_privateKey 缺失，请重新获取后重试", "");
        }
    } catch (error) {
        task.logger.error(`[douyinPublish] 解析 ec_privateKey 失败: ${error}`);
        return response(414, "安全参数 ec_privateKey 解析失败，请重新获取后重试", "");
    }

    let certificate: string;
    try {
        certificate = JSON.parse(params.extraParam["security-sdk/s_sdk_server_cert_key"] || "{}").cert;

        if (!certificate) {
            task.logger.error("[douyinPublish] certificate 为空");
            return response(414, "安全参数 certificate 缺失，请重新获取后重试", "");
        }
    } catch (error) {
        task.logger.error(`[douyinPublish] 解析 certificate 失败: ${error}`);
        return response(414, "安全参数 certificate 解析失败，请重新获取后重试", "");
    }

    const ree_public_key = webProtectData.client_cert.replace("pub.", "");

    let bdTicketGuardClientData: any;
    try {
        bdTicketGuardClientData = getBdV2({
            ticket,
            ts_sign: ts_sign,
            ec_privateKey: ec_privateKey,
            certificate: certificate,
            bd_ticket_guard_ree_public_key: ree_public_key,
        });

        if (!bdTicketGuardClientData || !bdTicketGuardClientData["headers_bd_ticket_guard_client_data"]) {
            task.logger.error(
                `[douyinPublish] bdTicketGuardClientData 生成失败或不完整: ${JSON.stringify(bdTicketGuardClientData)}`,
            );
            return response(414, "安全参数 bdTicketGuardClientData 生成失败，请重新获取后重试", "");
        }
    } catch (error) {
        task.logger.error(`[douyinPublish] 生成 bdTicketGuardClientData 失败: ${error}`);
        return response(414, "安全参数 bdTicketGuardClientData 生成异常，请重新获取后重试", "");
    }

    task.logger.info("[douyinPublish] bdTicketGuardClientData 生成成功");

    // 生成 aBogus
    const publishQuery = { ...publishParams, a_bogus: "" };
    const queryString = new URLSearchParams(publishQuery).toString();

    let aBogus: string;
    try {
        aBogus = getABogus(
            `https://creator.douyin.com/web/api/media/aweme/create_v2/?${queryString}`,
            publishData,
            userAgent,
        );

        if (!aBogus || aBogus.length < 10) {
            task.logger.error(`[douyinPublish] a_bogus 生成异常，长度过短: ${aBogus}`);
            return response(414, "安全参数 a_bogus 生成失败，请重试", "");
        }
    } catch (error) {
        task.logger.error(`[douyinPublish] 生成 a_bogus 失败: ${error}`);
        return response(414, "安全参数 a_bogus 生成异常，请重试", "");
    }

    publishQuery.a_bogus = aBogus;
    const publishQueryParams = new URLSearchParams(publishQuery).toString();

    // 生成 CSRF Token
    let csrfToken: string;
    try {
        csrfToken = generateCsrfTokenAdvanced({
            cookies: params.cookies,
            url: "https://creator.douyin.com/web/api/media/aweme/create_v2/",
            method: "POST",
            userAgent,
        });

        if (!csrfToken || csrfToken.length < 10) {
            task.logger.error(`[douyinPublish] csrfToken 生成异常，长度过短: ${csrfToken}`);
            return response(414, "安全参数 csrfToken 生成失败，请重试", "");
        }

        task.logger.info(`[douyinPublish] csrfToken 生成成功: ${csrfToken.substring(0, 20)}...`);
    } catch (error) {
        task.logger.error(`[douyinPublish] 生成 csrfToken 失败: ${error}`);
        return response(414, "安全参数 csrfToken 生成异常，请重试", "");
    }

    // 生成 sessionDtrait
    let sessionDtrait: string;
    try {
        sessionDtrait = generateDtrait("/web/api/media/aweme/create_v2/");
        task.logger.info(`[douyinPublish] sessionDtrait 生成成功: ${sessionDtrait || "(空)"}`);
    } catch (error) {
        task.logger.error(`[douyinPublish] 生成 sessionDtrait 失败: ${error}`);
        return response(414, "安全参数 sessionDtrait 生成异常，请重试", "");
    }

    // task.logger.info("[douyinPublish] 发布数据", { data: JSON.stringify(publishData) });
    task.logger.info(
        `[douyinPublish] headers: ${JSON.stringify({
            ...headers,
            Referer:
                "https://creator.douyin.com/creator-micro/content/post/image?default-tab=3&enter_from=publish_page&media_type=image&type=new",
            "Content-Type": "application/json",
            Accept: "application/json, text/plain, */*",
            "bd-ticket-guard-client-data": bdTicketGuardClientData["headers_bd_ticket_guard_client_data"],
            "bd-ticket-guard-ree-public-key": bdTicketGuardClientData["headers_bd_ticket_guard_ree_public_key"],
            "bd-ticket-guard-version": "2",
            "bd-ticket-guard-web-sign-type": "1",
            "bd-ticket-guard-web-version": "2",
            "x-secsdk-csrf-token": csrfToken,
            "x-tt-session-dtrait": sessionDtrait || "",
        })}`,
    );
    task.logger.info(
        `[douyinPublish] url: ${`https://creator.douyin.com/web/api/media/aweme/create_v2/?${publishQueryParams}`}`,
    );

    let publishResult: CommonResponse<{ aweme_id: string }>;
    try {
        publishResult = await proxyHttp.api<CommonResponse<{ aweme_id: string }>>(
            {
                method: "post",
                url: `https://creator.douyin.com/web/api/media/aweme/create_v2/?${publishQueryParams}`,
                data: publishData,
                defaultErrorMsg: "发布异常，请稍后重试。",
                headers: {
                    ...headers,
                    Referer:
                        "https://creator.douyin.com/creator-micro/content/post/image?default-tab=3&enter_from=publish_page&media_type=image&type=new",
                    "Content-Type": "application/json",
                    Accept: "application/json, text/plain, */*",
                    "bd-ticket-guard-client-data": bdTicketGuardClientData["headers_bd_ticket_guard_client_data"],
                    "bd-ticket-guard-ree-public-key": bdTicketGuardClientData["headers_bd_ticket_guard_ree_public_key"],
                    "bd-ticket-guard-version": "2",
                    "bd-ticket-guard-web-sign-type": "1",
                    "bd-ticket-guard-web-version": "2",
                    "x-secsdk-csrf-token": csrfToken,
                    "x-tt-session-dtrait": sessionDtrait || "",
                },
            },
            { timeout: 60 * 1000, retries: 3, retryDelay: 2000 },
        );
    } catch (error) {
        const handledError = Http.handleApiError(error);
        // 代理连接超时(599) 与网络/服务端错误(500/502/503/504)：重试耗尽后直接返回代理错误信息，
        // 不再 throw 走 executeAction 的 RPA 兜底。code 归一为 414，使 executeAction 视为终止结果。
        const isProxyOrNetworkError = [599, 500, 502, 503, 504].includes(handledError.code);
        if (isProxyOrNetworkError) {
            // 区分代理错误和普通网络错误
            const isProxyRequest = handledError.extra?.isProxyRequest === true;
            const errorType = handledError.code === 599 || isProxyRequest ? "代理错误" : "网络错误";
            const message = `图文发布失败，${errorType}：${handledError.message}${task.debug ? ` ${http.proxyInfo}` : ""}`;
            task.logger.error(`[douyinPublish] ${errorType}，直接返回: ${message}`, stringifyError(handledError));
            await updateTaskState?.({
                state: TaskState.FAILED,
                error: message,
            });
            return { code: 414, data: "", message };
        }
        // 其它错误码维持原有逻辑，抛出以便上层 executeAction 走 RPA 兜底
        throw error;
    }

    task.logger.info(
        `[douyinPublish] decision值: ${JSON.stringify(decision)}, needsVerification(decision): ${needsVerification(decision)}`,
    );
    if (needsVerification(decision)) {
        const details = [
            publishResult.status_msg,
            (decision as ParsedVerifyDecision | null)?.verify_title,
            (decision as ParsedVerifyDecision | null)?.verify_desc,
        ]
            .filter(Boolean)
            .join(" ");
        const message = `图文发布失败，原因：${details}${task.debug ? ` ${http.proxyInfo}` : ""}`;
        let decisionObj = {};
        if (decision) {
            decisionObj = decision;
        }
        const mockData = {
            state: TaskState.FAILED,
            error: message,
            result: {
                ...decisionObj,
                data: decision,
                uploadedImgList: JSON.stringify((publishData.item as any).common.images),
            },
        };
        await updateTaskState?.(mockData);
        return { code: 0, data: mockData, message: "操作成功" };
    }

    reportLogger({
        token: params.huiwenToken || "",
        enverionment: task.enverionment || "development",
        postId: params.articleId,
        eip: proxyHttp.proxyInfo,
        proxyIp: params.localIP,
        accountId: params.accountId,
        uid: params.uid,
        platform: "douyin",
        publishParams: publishData,
    });
    task.logger.info(`[douyinPublish] 发布结果 ${JSON.stringify(publishResult)}`);
    const isSuccess = publishResult.status_code === 0;
    const message = `图文发布${isSuccess ? "成功" : `失败，原因：${publishResult.status_msg} ${(decision as ParsedVerifyDecision | null)?.verify_title} ${(decision as ParsedVerifyDecision | null)?.verify_desc}`}${task.debug ? ` ${http.proxyInfo}` : ""}`;
    const data = isSuccess ? publishResult.item_id || "" : "";
    if (!isSuccess) {
        let decisionObj = {};
        if (decision) {
            decisionObj = decision;
        }
        await updateTaskState?.({
            state: TaskState.FAILED,
            error: message,
            result: {
                ...decisionObj,
                data: decision,
                uploadedImgList: JSON.stringify((publishData.item as any).common.images),
            },
        });
        return { code: 414, data, message };
    }

    await updateTaskState?.({
        state: TaskState.SUCCESS,
        result: {
            response: data,
        },
    });

    return response(isSuccess ? 0 : 414, message, data);
};
